import { useMemo, useState } from "react";
import { Loader2, NotebookPen, Search } from "lucide-react";
import { useSearchParams } from "react-router";
import { useProjectContext } from "@/app/project-context";
import { EmptyState } from "@/components/gb/empty-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUrlSearch } from "@/hooks/use-url-search";
import { addDays, DISPLAY_LOCALE } from "@/lib/format";
import { CatchUpQueue } from "./catch-up-queue";
import {
  JournalCalendar,
  resolveWindowStart,
  todayIso,
} from "./journal-calendar";
import { JournalComposer } from "./journal-composer";
import { JournalFeed } from "./journal-feed";
import { useJournal, useJournalDays, useJournalNotes } from "./journals-api";

/**
 * The Journal: a full-width header over a two-pane body.
 *
 * **Exactly one element scrolls per pane, and the page never grows.** The rail
 * and the feed each carry `min-h-0` and their own `overflow-y-auto` inside the
 * single shell column, which is what `chat-screen.tsx` does and what
 * `architecture/0001-app-shell-scrolling.md` requires. The diagnostic when it
 * goes wrong is worth keeping to hand: `contain: paint` on the shell column
 * fixes it and `overflow: hidden` does not, because the culprit is always an
 * absolutely positioned descendant escaping the clip.
 *
 * Nothing here is `position: sticky`. The new-entry prompt is "pinned" by being
 * first in the feed (§7.6) — sticky positioning inside the scroll column is
 * untested in this app and this is not the feature to find out what it does.
 *
 * Neither Export nor Generate report appears, in any form. JRNL-04 excludes
 * both, and a disabled Export invites a support question with no answer (§7.5).
 */

// ------------------------------------------------------------------
// URL state
// ------------------------------------------------------------------

export type DateRange = { from: string; to: string };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isIsoDate(value: string | null): value is string {
  return value !== null && ISO_DATE.test(value);
}

function utcDate(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

/** The calendar month `iso` falls in, first day to last. */
export function monthRange(iso: string): DateRange {
  const day = utcDate(iso);
  const year = day.getUTCFullYear();
  const month = day.getUTCMonth();
  return {
    from: new Date(Date.UTC(year, month, 1)).toISOString().slice(0, 10),
    to: new Date(Date.UTC(year, month + 1, 0)).toISOString().slice(0, 10),
  };
}

/**
 * The range the right pane shows, read out of the URL.
 *
 * Three cases, in order:
 *
 *  - `?from=&to=` — a range the user picked on the calendar, which is in the
 *    URL so a range is linkable and survives a reload.
 *  - `?date=` — **a citation**. The chat cites a note as
 *    `/projects/:projectId/journal?date=YYYY-MM-DD` (`chat-links.ts`), and
 *    without this the link lands on the Journal with no day selected, which
 *    reads as a citation that goes nowhere.
 *  - neither — the current month.
 *
 * Derived from the URL rather than copied into state on mount, so following a
 * second citation while already on this screen re-selects the new day instead
 * of quietly doing nothing.
 */
export function rangeFromParams(
  params: { from: string | null; to: string | null; date: string | null },
  today: string,
): DateRange {
  if (isIsoDate(params.from) && isIsoDate(params.to)) {
    // Tolerate a reversed pair rather than showing an empty range: it is a
    // hand-edited URL, and the user's intent is unambiguous.
    return params.from <= params.to
      ? { from: params.from, to: params.to }
      : { from: params.to, to: params.from };
  }
  if (isIsoDate(params.date)) return { from: params.date, to: params.date };
  return monthRange(today);
}

/**
 * Days inside the range that hold nothing — the header's "1 day not logged".
 *
 * Derived from *absence*: a date the day-summary response does not mention has
 * no row anywhere, and none is written to make it countable
 * (`data-model/0004` §1). Clipped to the window at one end and to today at the
 * other, because a future date and a date before the site existed are not gaps.
 *
 * `logged` is the set of dates with `noteCount > 0`, not the set of dates the
 * response returned — a date that came back only because photographs were
 * dropped on it still holds nothing anybody wrote, and the calendar and the
 * catch-up queue both count it as a gap. Three surfaces, one rule.
 */
export function notLoggedInRange(
  range: DateRange,
  logged: ReadonlySet<string>,
  windowStart: string,
  today: string,
): number {
  const from = range.from > windowStart ? range.from : windowStart;
  const to = range.to < today ? range.to : today;
  if (from > to) return 0;

  let count = 0;
  for (let date = from; date <= to; date = addDays(date, 1)) {
    if (!logged.has(date)) count += 1;
  }
  return count;
}

/** `Wednesday, 3 June 2026`, `1 – 30 June 2026`, `28 May – 30 June 2026`. */
export function formatRangeLabel(from: string, to: string): string {
  if (from === to) {
    return utcDate(from).toLocaleDateString(DISPLAY_LOCALE, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  }

  const start = utcDate(from);
  const end = utcDate(to);
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();

  const left = start.toLocaleDateString(DISPLAY_LOCALE, {
    day: "numeric",
    ...(sameMonth ? {} : { month: "long" as const }),
    ...(sameYear ? {} : { year: "numeric" as const }),
    timeZone: "UTC",
  });
  const right = end.toLocaleDateString(DISPLAY_LOCALE, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  return `${left} – ${right}`;
}

/**
 * How far back the day-summary query reaches when the project has no start
 * date.
 *
 * The calendar can page back through the whole life of a site, and
 * `GET /journal/days` answers with **only the dates that hold something** — so
 * a wide window costs one row per recorded day rather than one per day in it.
 */
const NO_START_DATE_LOOKBACK_DAYS = 5 * 365;

// ------------------------------------------------------------------
// The screen
// ------------------------------------------------------------------

export function JournalScreen() {
  const { project, projectId } = useProjectContext();
  const [params, setParams] = useSearchParams();

  // Recomputed per render, as the calendar and the queue do — pinning it at
  // mount would let a tab left open overnight disagree with the grid about
  // which day is Today. It is a string, so the memos below still compare it by
  // value and do not re-run.
  const today = todayIso();

  const fromParam = params.get("from");
  const toParam = params.get("to");
  const dateParam = params.get("date");
  const selected = useMemo(
    () =>
      rangeFromParams({ from: fromParam, to: toParam, date: dateParam }, today),
    [fromParam, toParam, dateParam, today],
  );

  // Debounced and mirrored into `?q=`, the way the Brain screen does it — the
  // hook exists because the obvious version of this loops.
  const { term, setTerm, debounced } = useUrlSearch("q");

  /** Which date the composer is open for, or null when it is closed. */
  const [composingDate, setComposingDate] = useState<string | null>(null);

  const daysWindow = useMemo(
    () => ({
      from: project.startDate ?? addDays(today, -NO_START_DATE_LOOKBACK_DAYS),
      // Future dates are not selectable in the composer, so nothing can be
      // filed beyond today and there is nothing past it to fetch.
      to: today,
    }),
    [project.startDate, today],
  );

  const days = useJournalDays(projectId, daysWindow);
  const noteRange = useMemo(
    () => ({ from: selected.from, to: selected.to, q: debounced || undefined }),
    [selected.from, selected.to, debounced],
  );
  const notes = useJournalNotes(projectId, noteRange);

  // The day headers link to `/journal/:entryId`, and neither a note nor a day
  // summary carries that id — only the entry list does. A day with no
  // `journal_entries` row simply has no chevron; opening one would mean
  // *writing* a row for a day nobody recorded.
  const entries = useJournal(projectId);
  const entryIdByDate = useMemo(
    () => new Map((entries.data ?? []).map((entry) => [entry.date, entry.id])),
    [entries.data],
  );

  const allDays = days.data?.days ?? [];
  const daysByDate = useMemo(
    () => new Map(allDays.map((day) => [day.date, day])),
    [allDays],
  );

  const windowStart = resolveWindowStart(
    days.data?.windowStart ?? null,
    allDays,
  );
  const daysInRange = allDays.filter(
    (day) => day.date >= selected.from && day.date <= selected.to,
  );
  const entryCount = daysInRange.reduce(
    (total, day) => total + day.entryCount,
    0,
  );
  const notLogged = notLoggedInRange(
    selected,
    new Set(allDays.filter((day) => day.noteCount > 0).map((day) => day.date)),
    windowStart,
    today,
  );

  /**
   * The date the feed's pinned prompt files against — **the day the reader is
   * looking at**, not always today.
   *
   * It was `today` unconditionally, which was invisible while the prompt only
   * rendered for an unlogged today. Once it rendered always, selecting 4 August
   * left a prompt reading "Add a note — Wednesday 5 August" sitting above an
   * empty 4 August, and it filed against the 5th.
   *
   * Three cases:
   *  - the range covers today — today, wherever else it reaches;
   *  - a single past day is selected — that day, which is the only affordance
   *    it has: an empty day emits no gap row, because gaps are only derived
   *    *between* days that hold notes;
   *  - a past span — `null`. No one date is the obvious subject of a fortnight,
   *    and each day in it carries its own `Add note` in its header.
   *
   * Never a future date. The composer caps its picker at today, and a prompt
   * offering a day it would then refuse is a broken button.
   */
  const promptDate =
    selected.from <= today && today <= selected.to
      ? today
      : selected.from === selected.to && selected.from < today
        ? selected.from
        : null;
  const searching = debounced.trim().length > 0;
  const journalIsEmpty =
    allDays.length === 0 && (notes.data?.length ?? 0) === 0;

  const selectRange = (range: DateRange): void => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("from", range.from);
        next.set("to", range.to);
        // A citation's `?date=` has done its job the moment the user picks
        // something else; leaving it behind would make it win again on the
        // next render.
        next.delete("date");
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="mx-auto flex h-full max-w-[1180px] flex-col px-[30px] pt-4 pb-6">
      {/* No heading. The topbar already reads "ASHFIELD · DAILY RECORD /
          JOURNAL" on every screen, so an `<h1>` here said "Journal" twice, one
          above the other. The search box keeps the row. */}
      <header className="mb-4 flex shrink-0 flex-wrap items-center justify-end gap-3">
        {/* `relative` so the icon is positioned against this box and not
            against the document — `architecture/0001` in miniature. */}
        <div className="relative w-full max-w-[320px]">
          <span className="pointer-events-none absolute top-1/2 left-[14px] flex -translate-y-1/2 text-muted">
            <Search size={16} />
          </span>
          <Input
            withIcon
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            // Substring matching, not retrieval. The field says "Search", not
            // "Ask", and the placeholder says how much it is searching.
            placeholder={
              days.data
                ? `Search ${entryCount} ${entryLabel(entryCount)}…`
                : "Search entries…"
            }
            aria-label="Search this site’s journal"
          />
        </div>
      </header>

      <div className="flex min-h-0 flex-1 gap-6">
        {/* `min-h-0` and the pane's own `overflow-y-auto`, per
            `architecture/0001`: the rail scrolls inside the shell column
            rather than making the column — and with it the page — taller. */}
        <aside className="hidden min-h-0 w-[300px] shrink-0 flex-col gap-4 overflow-y-auto pr-1 lg:flex">
          {days.isPending ? (
            <PaneLoading label="Loading the calendar…" />
          ) : days.isError ? (
            <PaneError
              title="Couldn’t load the calendar."
              error={days.error}
              onRetry={() => void days.refetch()}
            />
          ) : (
            <>
              <JournalCalendar
                days={allDays}
                windowStart={days.data.windowStart}
                selected={selected}
                onSelect={selectRange}
              />
              <CatchUpQueue
                days={allDays}
                windowStart={days.data.windowStart}
                onFillIn={(date) => setComposingDate(date)}
              />
            </>
          )}
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mb-3 flex shrink-0 items-start gap-4">
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-[1.05rem] font-semibold tracking-[0.02em] text-ink">
                {formatRangeLabel(selected.from, selected.to)}
              </h2>
              <p className="mt-0.5 text-[0.85rem] text-muted">
                {days.data ? (
                  <>
                    {entryCount} {entryLabel(entryCount)} · {notLogged}{" "}
                    {notLogged === 1 ? "day" : "days"} not logged
                  </>
                ) : (
                  "Counting what this range holds…"
                )}
              </p>
            </div>

            {/* Rendered and inert, as the design draws them. JRNL-04 excludes
                both from this release, and `prd-brain-sources` §7 is the house
                precedent for a designed control that is not wired: it renders
                exactly as designed, and does nothing. `disabled` plus a title
                rather than a hidden button, because the design shows the
                affordance and a reader should be able to see that the feature
                is coming rather than wonder where it went. */}
            <div className="flex shrink-0 gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled
                title="Not available yet"
              >
                Export
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled
                title="Not available yet"
              >
                Generate report
              </Button>
            </div>
          </div>
          <div className="mb-3 shrink-0">
            {searching && (
              <p className="mt-0.5 text-[0.85rem] text-muted">
                Showing notes that mention &ldquo;{debounced}&rdquo;.
              </p>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            {notes.isPending ? (
              <PaneLoading label="Loading the journal…" />
            ) : notes.isError ? (
              <PaneError
                title="Couldn’t load the journal."
                error={notes.error}
                onRetry={() => void notes.refetch()}
              />
            ) : journalIsEmpty && !searching ? (
              <EmptyState
                icon={NotebookPen}
                title="Nothing filed against this site yet"
                description="A note is anything worth recording — a delivery at 07:40, a hold-up, a near-miss, or the whole day written up before you drive home. File as many against a date as the day needs."
                action={
                  <Button
                    className="hover:bg-brand-deep"
                    onClick={() => setComposingDate(today)}
                  >
                    <NotebookPen size={16} />
                    Log site note
                  </Button>
                }
              />
            ) : (
              <JournalFeed
                projectId={projectId}
                notes={notes.data}
                daysByDate={daysByDate}
                today={today}
                promptDate={promptDate}
                entryIdByDate={entryIdByDate}
                searching={searching}
                onCompose={(date) => setComposingDate(date)}
              />
            )}
          </div>
        </div>
      </div>

      {/* One composer serves every entry point into this screen — the prompt,
          the queue's Fill in, and a backfill on a past day. */}
      <JournalComposer
        projectId={projectId}
        date={composingDate ?? today}
        open={composingDate !== null}
        onOpenChange={(open) => {
          if (!open) setComposingDate(null);
        }}
        onSaved={() => setComposingDate(null)}
      />
    </div>
  );
}

function entryLabel(count: number): string {
  return count === 1 ? "entry" : "entries";
}

function PaneLoading({ label }: { label: string }) {
  return (
    <div
      className="flex items-center gap-2 py-12 text-muted"
      role="status"
      aria-live="polite"
    >
      <Loader2 size={16} className="animate-spin" />
      <span className="text-[0.9rem]">{label}</span>
    </div>
  );
}

function PaneError({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-card-lg border-[1.5px] border-danger/40 bg-danger-bg px-5 py-4">
      <p className="text-[0.92rem] font-semibold text-danger">{title}</p>
      <p className="mt-1 text-[0.85rem] text-ink-slate">
        {error instanceof Error ? error.message : "Something went wrong."}
      </p>
      <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

/**
 * `journal-entry-screen.tsx` has always imported this from here. The definition
 * moved next to the feed, where days are actually rendered; the name stays put
 * so that screen — which is not part of this change — does not move with it.
 */
export { formatJournalDate } from "./journal-feed";
